import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, Phone, Check, Loader2 } from "lucide-react";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";
import api from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

const LINES = [
  { role: "agent", name: "Ava", text: "Thank you for calling Richmond Vona, this is Ava. How can I help you today?" },
  { role: "caller", text: "Hi, I was in a car accident last week and I don't know what to do." },
  { role: "agent", text: "I'm so sorry that happened. I can help you get started — can I get your name?" },
  { role: "caller", text: "It's Marcus Bell." },
  { role: "agent", text: "Thank you, Marcus. Was anyone injured, and have you seen a doctor?" },
  { role: "caller", text: "Yeah, my neck's been hurting. Actually — can I just talk to a real person?" },
  { role: "agent", text: "Of course. Let me connect you with our intake team right now — I'll pass along everything you've told me, so you won't have to repeat yourself." },
];

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, "0")}`;
};

const Field = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-paper/45">
      {label}
    </span>
    {value ? (
      <span className="inline-flex items-center gap-1.5 font-sans text-[13px] text-[#93B4FF]">
        <span className="w-1.5 h-1.5 rounded-full bg-accent" />
        {value}
      </span>
    ) : (
      <span className="font-sans text-[13px] text-white/25">—</span>
    )}
  </div>
);

const ScriptedPlaybackSlide = ({ slide }) => {
  const reduce = useReducedMotion();

  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // Continuous time model
  const DUR = React.useMemo(
    () => LINES.map((l) => Math.max(1400, l.text.length * 52) / 1000),
    []
  );
  const STARTS = React.useMemo(() => {
    const s = [];
    let acc = 0;
    DUR.forEach((d) => {
      s.push(acc);
      acc += d;
    });
    return s;
  }, [DUR]);
  const TOTAL = React.useMemo(() => DUR.reduce((a, b) => a + b, 0), [DUR]);

  const startRef = useRef(0);
  const rafRef = useRef(null);
  const spokenRef = useRef(-1);
  const convRef = useRef(null);
  const synth = typeof window !== "undefined" ? window.speechSynthesis : null;

  const stopRaf = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const speak = useCallback(
    (line) => {
      if (!synth) return;
      try {
        synth.cancel();
        const u = new SpeechSynthesisUtterance(line.text);
        const voices = synth.getVoices();
        if (voices.length) {
          const agentV =
            voices.find((v) => /Samantha|Zira|female|Google US English/i.test(v.name)) ||
            voices[0];
          const callerV =
            voices.find((v) => /Daniel|Alex|male|Google UK English Male/i.test(v.name)) ||
            voices[voices.length - 1];
          u.voice = line.role === "agent" ? agentV : callerV;
        }
        u.rate = 1.03;
        u.pitch = line.role === "agent" ? 1.06 : 0.94;
        synth.speak(u);
      } catch (e) {
        /* best-effort audio */
      }
    },
    [synth]
  );

  const loop = useCallback(() => {
    const t = (performance.now() - startRef.current) / 1000;
    if (t >= TOTAL) {
      setElapsed(TOTAL);
      setPlaying(false);
      setEnded(true);
      stopRaf();
      return;
    }
    setElapsed(t);
    rafRef.current = requestAnimationFrame(loop);
  }, [TOTAL]);

  const play = () => {
    let base = elapsed;
    if (ended || elapsed >= TOTAL) {
      base = 0;
      setEnded(false);
      setElapsed(0);
    }
    startRef.current = performance.now() - base * 1000;
    spokenRef.current = -1;
    setPlaying(true);
    stopRaf();
    rafRef.current = requestAnimationFrame(loop);
  };
  const pause = () => {
    stopRaf();
    if (synth) synth.cancel();
    setPlaying(false);
  };
  const toggle = () => (playing ? pause() : play());

  const seek = (t) => {
    const clamped = Math.max(0, Math.min(t, TOTAL));
    if (synth) synth.cancel();
    spokenRef.current = -1;
    startRef.current = performance.now() - clamped * 1000;
    setElapsed(clamped);
    setEnded(clamped >= TOTAL);
  };

  const toggleMute = () => {
    setMuted((m) => {
      const nm = !m;
      if (nm && synth) synth.cancel();
      return nm;
    });
  };

  // Reduced motion: reveal everything at once.
  useEffect(() => {
    if (reduce) {
      setElapsed(TOTAL);
      setEnded(true);
    }
    return () => {
      stopRaf();
      if (synth) synth.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived timeline state from elapsed.
  let dActive = -1;
  let dReached = -1;
  for (let i = 0; i < LINES.length; i++) {
    if (elapsed >= STARTS[i]) dReached = i;
    if (elapsed >= STARTS[i] && elapsed < STARTS[i] + DUR[i]) dActive = i;
  }
  if (ended) dActive = -1;
  const activeLine = dActive;
  const reachedLine = dReached;
  const isSpeaking = playing && dActive >= 0;

  // Speak when the active line changes during playback.
  useEffect(() => {
    if (playing && !muted && activeLine >= 0 && activeLine !== spokenRef.current) {
      spokenRef.current = activeLine;
      speak(LINES[activeLine]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLine, playing, muted]);

  // auto-scroll conversation to the active bubble
  useEffect(() => {
    if (convRef.current) convRef.current.scrollTop = convRef.current.scrollHeight;
  }, [reachedLine]);

  // Aliases used by the JSX below.
  const active = activeLine;
  const reached = reachedLine;
  const speakingNow = isSpeaking;

  // Derived system state
  const status =
    reached >= 6 ? "Warm transfer → Intake" : reached >= 0 ? "On call · Reception" : "Idle";
  const fType = reached >= 1 ? "New client" : null;
  const fMatter = reached >= 1 ? "Auto / MVA" : null;
  const fName = reached >= 3 ? "Marcus Bell" : null;
  const fInjury = reached >= 5 ? "Neck pain" : null;
  const fCallback = reached >= 5 ? "(408) ···-··21" : null;
  const showTransfer = reached >= 5;
  const showLogged = reached >= 6;
  const dotLive = playing;
  const progressPct = TOTAL ? (Math.min(elapsed, TOTAL) / TOTAL) * 100 : 0;


  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  return (
    <Stage testId={`slide-${slide?.n ?? 6}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(20px,4%,44px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>Live demo</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            07 / 11
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-3"
        >
          Hear it. See it.
        </motion.h1>

        {/* Subhead */}
        <motion.p
          variants={item}
          className="font-sans text-[14px] text-ink-soft leading-relaxed mt-2.5 max-w-[66ch]"
        >
          A real inbound call: the agent answers as your firm, captures intake, and
          logs everything to your CMS as it happens. Ask for a human anytime — it
          hands off warmly, with full context passed along.
        </motion.p>

        {/* Body */}
        <div className="flex-1 min-h-0 grid grid-cols-1 min-[900px]:grid-cols-[1.08fr_0.92fr] gap-5 mt-5">
          {/* LEFT — player */}
          <motion.div
            variants={item}
            className="rounded-2xl border border-hairline bg-[#FBFAF6] p-5 flex flex-col min-h-0 shadow-[0_1px_3px_rgba(35,31,35,0.04),0_12px_30px_-18px_rgba(35,31,35,0.16)]"
          >
            {/* header */}
            <div className="flex items-center gap-3">
              <button
                data-testid="play-toggle"
                onClick={toggle}
                aria-label={playing ? "Pause" : "Play"}
                className="w-11 h-11 rounded-full bg-accent text-white flex items-center justify-center shrink-0 transition-transform duration-200 hover:scale-105"
              >
                {playing ? (
                  <Pause className="w-5 h-5" fill="currentColor" />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="font-sans text-sm font-medium text-ink leading-tight">
                  Inbound call — new client intake
                </div>
                <div className="font-mono text-[11px] text-ink-muted mt-0.5">
                  Richmond Vona · Reception agent 'Ava'
                </div>
              </div>
              <span
                data-testid="call-timer"
                className="font-mono text-sm text-ink-soft tabular-nums"
              >
                {fmt(elapsed)}
              </span>
              <button
                data-testid="mute-toggle"
                onClick={toggleMute}
                className="inline-flex items-center gap-1.5 rounded-full border border-hairline px-2.5 py-1 hover:bg-ink/[0.03] transition-colors"
              >
                {muted ? (
                  <VolumeX className="w-3.5 h-3.5 text-ink-muted" />
                ) : (
                  <Volume2 className="w-3.5 h-3.5 text-accent" />
                )}
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-soft">
                  {muted ? "Muted" : "Sound"}
                </span>
              </button>
            </div>

            {/* waveform */}
            <div
              className={`flex items-center justify-between gap-[2px] h-9 mt-4 ${
                speakingNow ? "wave-on" : ""
              }`}
            >
              {Array.from({ length: 44 }).map((_, i) => (
                <span
                  key={i}
                  className="wave-bar flex-1 h-full rounded-full bg-accent/80"
                  style={{ animationDelay: `${((i % 12) * 0.06).toFixed(2)}s` }}
                />
              ))}
            </div>

            {/* scrubber */}
            <div className="mt-3 flex items-center gap-3">
              <input
                data-testid="scrubber"
                type="range"
                min={0}
                max={TOTAL}
                step={0.05}
                value={Math.min(elapsed, TOTAL)}
                onChange={(e) => seek(Number(e.target.value))}
                aria-label="Seek within the call"
                className="range-lex flex-1"
                style={{
                  background: `linear-gradient(to right, #1E5BFF 0%, #1E5BFF ${progressPct}%, #E3E1DE ${progressPct}%, #E3E1DE 100%)`,
                }}
              />
              <span className="font-mono text-[11px] text-ink-muted tabular-nums shrink-0">
                {fmt(TOTAL)}
              </span>
            </div>

            {/* conversation */}
            <div
              ref={convRef}
              data-testid="conversation"
              className="mt-4 flex-1 min-h-0 overflow-y-auto space-y-2.5 pr-1"
            >
              {LINES.map((line, i) => {
                if (i > reached) return null;
                const isAgent = line.role === "agent";
                const current = i === active;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: reduce ? 0 : 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: reduce ? 0.001 : 0.3, ease: EASE }}
                    className={`flex items-end gap-2 ${isAgent ? "justify-start" : "justify-end"}`}
                    data-testid={`bubble-${i + 1}`}
                  >
                    {isAgent ? (
                      <span className="w-6 h-6 rounded-full bg-accent text-white text-[11px] font-semibold flex items-center justify-center shrink-0">
                        A
                      </span>
                    ) : null}
                    <div
                      className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug ${
                        isAgent
                          ? "bg-white border border-hairline text-ink rounded-bl-sm"
                          : "bg-accent text-white rounded-br-sm"
                      } ${current ? "ring-2 ring-accent ring-offset-1 ring-offset-[#FBFAF6]" : ""}`}
                    >
                      {line.text}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* footer */}
            <div className="mt-3 pt-3 border-t border-dashed border-hairline">
              <p className="font-sans text-[12px] text-ink-muted">
                <span className="font-semibold text-ink-soft">Trapdoor:</span> a
                caller can ask for a person at any point — the agent transfers
                warmly.
              </p>
            </div>
          </motion.div>

          {/* RIGHT — system panel */}
          <motion.div
            variants={item}
            data-testid="system-panel"
            className="rounded-2xl p-5 flex flex-col min-h-0 text-paper bg-[linear-gradient(155deg,#191622_0%,#211D2A_100%)]"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-paper/55">
                What the system is doing
              </span>
              <span
                data-testid="system-status"
                className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-2.5 py-1"
              >
                <span className="relative flex w-2 h-2">
                  {dotLive ? (
                    <span className="live-ping absolute inline-flex w-full h-full rounded-full bg-accent" />
                  ) : null}
                  <span
                    className={`relative inline-flex w-2 h-2 rounded-full ${
                      reached >= 0 ? "bg-accent" : "bg-white/30"
                    }`}
                  />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#93B4FF]">
                  {status}
                </span>
              </span>
            </div>

            {/* cards */}
            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5">
                <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-paper/45 mb-1">
                  Caller
                </div>
                <Field label="Type" value={fType} />
                <Field label="Matter" value={fMatter} />
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5">
                <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-paper/45 mb-1">
                  Intake record → CMS
                </div>
                <Field label="Name" value={fName} />
                <Field label="Injury" value={fInjury} />
                <Field label="Callback" value={fCallback} />
              </div>
            </div>

            {/* warm transfer banner */}
            <div className="mt-auto pt-4">
              {showTransfer ? (
                <motion.div
                  initial={{ opacity: 0, y: reduce ? 0 : 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reduce ? 0.001 : 0.4, ease: EASE }}
                  data-testid="transfer-banner"
                  className="rounded-xl border border-accent/30 bg-accent/[0.12] px-4 py-3"
                >
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-[#93B4FF]" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#93B4FF]">
                      Human requested → warm transfer
                    </span>
                  </div>
                  <p className="font-sans text-[12px] text-paper/75 mt-1.5 leading-snug">
                    Connecting to the intake team, with the full conversation passed
                    along. No repeating.
                  </p>
                </motion.div>
              ) : null}

              {showLogged ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: reduce ? 0.001 : 0.4, delay: reduce ? 0 : 0.2 }}
                  data-testid="logged-note"
                  className="mt-2.5 inline-flex items-center gap-1.5"
                >
                  <Check className="w-3.5 h-3.5 text-accent" strokeWidth={3} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#93B4FF]">
                    Logged to CMS · context primed
                  </span>
                </motion.div>
              ) : null}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </Stage>
  );
};

// ---------------------------------------------------------------------------
// Live mode: plays the actual ElevenLabs recording from the Slide-5 demo call.
// ---------------------------------------------------------------------------
const SIGNALS = [
  { key: "connected", label: "Call connected", at: 0 },
  { key: "listening", label: "Transcribing live", at: 0.05 },
  { key: "intake", label: "Intake captured", at: 0.55 },
  { key: "logged", label: "Logged to CMS", at: 1 },
];

const LiveField = ({ label, value, loading }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-paper/45">
      {label}
    </span>
    {value ? (
      <motion.span
        initial={{ opacity: 0, x: 6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, ease: EASE }}
        className="inline-flex items-center gap-1.5 font-sans text-[13px] text-[#93B4FF] text-right"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
        {value}
      </motion.span>
    ) : loading ? (
      <span className="font-sans text-[12px] text-white/25 italic">analyzing…</span>
    ) : (
      <span className="font-sans text-[13px] text-white/25">—</span>
    )}
  </div>
);

const LiveCallSlide = ({ slide, demoCall }) => {
  const reduce = useReducedMotion();
  const [convId, setConvId] = useState(demoCall?.conversationId || null);
  const [phase, setPhase] = useState("processing"); // processing | ready | error
  const [transcript, setTranscript] = useState([]);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [fields, setFields] = useState(null);

  const isInbound = demoCall?.mode === "inbound";
  const audioRef = useRef(null);
  const convRef = useRef(null);
  const pollRef = useRef(null);
  const resolveRef = useRef(null);
  const urlRef = useRef(null);

  // Inbound: resolve the conversation id from the agent's latest inbound call.
  useEffect(() => {
    if (!isInbound || convId) return;
    const slug = demoCall?.agentSlug;
    if (!slug) return;
    let cancelled = false;

    const resolve = async () => {
      try {
        const res = await api.get(`/deck/latest-conversation`, {
          params: { agent_slug: slug },
        });
        if (cancelled) return;
        if (res.data.conversation_id) {
          if (resolveRef.current) clearInterval(resolveRef.current);
          setConvId(res.data.conversation_id);
        }
      } catch (e) {
        /* keep polling */
      }
    };

    resolve();
    resolveRef.current = setInterval(resolve, 4000);
    return () => {
      cancelled = true;
      if (resolveRef.current) clearInterval(resolveRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInbound, demoCall?.agentSlug, convId]);

  // Poll ElevenLabs (via backend) until the recording is ready, then load audio.
  useEffect(() => {
    const id = convId;
    if (!id) return;
    let cancelled = false;

    const loadRecording = async (dur, tr) => {
      try {
        const res = await api.get(`/deck/call-recording/${id}`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        urlRef.current = url;
        setAudioUrl(url);
        setTranscript(tr);
        setDuration(dur);
        setPhase("ready");
        try {
          const ex = await api.get(`/deck/call-extract/${id}`);
          if (!cancelled) setFields(ex.data);
        } catch (e) {
          /* extraction best-effort */
        }
      } catch (e) {
        if (!cancelled) setPhase("error");
      }
    };

    const tick = async () => {
      try {
        const res = await api.get(`/deck/call-status/${id}`);
        if (cancelled) return;
        const { status, has_audio, transcript: tr, duration: dur } = res.data;
        if (status === "failed") {
          setPhase("error");
          if (pollRef.current) clearInterval(pollRef.current);
          return;
        }
        if (status === "done" && has_audio) {
          if (pollRef.current) clearInterval(pollRef.current);
          await loadRecording(dur || 0, tr || []);
        }
      } catch (e) {
        /* keep polling */
      }
    };

    tick();
    pollRef.current = setInterval(tick, 4000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  const dur = duration || audioRef.current?.duration || 0;
  const progressPct = dur ? (Math.min(elapsed, dur) / dur) * 100 : 0;

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      a.play().catch(() => {});
    }
  };
  const seek = (t) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = t;
    setElapsed(t);
  };
  const toggleMute = () => {
    const a = audioRef.current;
    setMuted((m) => {
      const nm = !m;
      if (a) a.muted = nm;
      return nm;
    });
  };

  // Which transcript turns have been reached by the playhead.
  let reached = -1;
  for (let i = 0; i < transcript.length; i++) {
    if (elapsed >= (transcript[i].time || 0)) reached = i;
  }
  useEffect(() => {
    if (convRef.current) convRef.current.scrollTop = convRef.current.scrollHeight;
  }, [reached]);

  const frac = dur ? Math.min(elapsed / dur, 1) : 0;
  const ended = dur > 0 && elapsed >= dur - 0.2;

  // A field reveals when the playhead reaches its timestamp (or at call end if no timestamp).
  const fieldValue = (key) => {
    const f = fields && fields[key];
    if (!f || f.value == null) return null;
    if (f.at == null) return ended ? f.value : null;
    return elapsed >= f.at ? f.value : null;
  };
  const anyExtracting = convId && fields == null;

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  return (
    <Stage testId={`slide-${slide?.n ?? 6}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(20px,4%,44px)]"
      >
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>Your live call</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            07 / 11
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-3"
        >
          Hear your call. See the system work.
        </motion.h1>

        <motion.p
          variants={item}
          className="font-sans text-[14px] text-ink-soft leading-relaxed mt-2.5 max-w-[66ch]"
        >
          This is the actual call{" "}
          {isInbound ? "you just placed to the" : "the"}{" "}
          <span className="font-semibold text-ink">
            {demoCall?.agentName || "Lead Qualification"}
          </span>{" "}
          agent{isInbound ? "" : " just placed to you"} — the real recording, with
          the live transcript the system captured as it happened.
        </motion.p>

        <div className="flex-1 min-h-0 grid grid-cols-1 min-[900px]:grid-cols-[1.08fr_0.92fr] gap-5 mt-5">
          {/* LEFT — real player */}
          <motion.div
            variants={item}
            className="rounded-2xl border border-hairline bg-[#FBFAF6] p-5 flex flex-col min-h-0 shadow-[0_1px_3px_rgba(35,31,35,0.04),0_12px_30px_-18px_rgba(35,31,35,0.16)]"
          >
            {phase !== "ready" ? (
              <div
                data-testid="live-call-loading"
                className="flex-1 flex flex-col items-center justify-center text-center gap-3 py-10"
              >
                {phase === "error" ? (
                  <>
                    <Phone className="w-7 h-7 text-ink-muted" />
                    <p className="font-sans text-[14px] text-ink-soft max-w-[40ch]">
                      We couldn't load the recording for this call. It may still be
                      processing — try again in a moment.
                    </p>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-7 h-7 text-accent animate-spin" />
                    <p className="font-sans text-[14px] text-ink-soft max-w-[42ch]">
                      {isInbound
                        ? "Waiting for your dial-in call to finish — the recording appears the moment you hang up."
                        : "Finishing your call and preparing the recording… this appears the moment you hang up."}
                    </p>
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-muted">
                      {isInbound && !convId
                        ? "Listening for your call"
                        : "Waiting for the call to complete"}
                    </span>
                  </>
                )}
              </div>
            ) : (
              <>
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  data-testid="live-audio"
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  onTimeUpdate={(e) => setElapsed(e.currentTarget.currentTime)}
                  onLoadedMetadata={(e) => {
                    if (!duration) setDuration(e.currentTarget.duration || 0);
                  }}
                />
                <div className="flex items-center gap-3">
                  <button
                    data-testid="live-play-toggle"
                    onClick={toggle}
                    aria-label={playing ? "Pause" : "Play"}
                    className="w-11 h-11 rounded-full bg-accent text-white flex items-center justify-center shrink-0 transition-transform duration-200 hover:scale-105"
                  >
                    {playing ? (
                      <Pause className="w-5 h-5" fill="currentColor" />
                    ) : (
                      <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-sans text-sm font-medium text-ink leading-tight">
                      {isInbound ? "Inbound call" : "Outbound demo call"} — {demoCall?.agentName || "Lead Qualification"}
                    </div>
                    <div className="font-mono text-[11px] text-ink-muted mt-0.5">
                      Real ElevenLabs recording{demoCall?.phone ? ` · ${demoCall.phone}` : ""}
                    </div>
                  </div>
                  <span className="font-mono text-sm text-ink-soft tabular-nums">
                    {fmt(elapsed)}
                  </span>
                  <button
                    data-testid="live-mute-toggle"
                    onClick={toggleMute}
                    className="inline-flex items-center gap-1.5 rounded-full border border-hairline px-2.5 py-1 hover:bg-ink/[0.03] transition-colors"
                  >
                    {muted ? (
                      <VolumeX className="w-3.5 h-3.5 text-ink-muted" />
                    ) : (
                      <Volume2 className="w-3.5 h-3.5 text-accent" />
                    )}
                    <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-soft">
                      {muted ? "Muted" : "Sound"}
                    </span>
                  </button>
                </div>

                <div
                  className={`flex items-center justify-between gap-[2px] h-9 mt-4 ${
                    playing ? "wave-on" : ""
                  }`}
                >
                  {Array.from({ length: 44 }).map((_, i) => (
                    <span
                      key={i}
                      className="wave-bar flex-1 h-full rounded-full bg-accent/80"
                      style={{ animationDelay: `${((i % 12) * 0.06).toFixed(2)}s` }}
                    />
                  ))}
                </div>

                <div className="mt-3 flex items-center gap-3">
                  <input
                    data-testid="live-scrubber"
                    type="range"
                    min={0}
                    max={dur || 0}
                    step={0.05}
                    value={Math.min(elapsed, dur || 0)}
                    onChange={(e) => seek(Number(e.target.value))}
                    aria-label="Seek within the call"
                    className="range-lex flex-1"
                    style={{
                      background: `linear-gradient(to right, #1E5BFF 0%, #1E5BFF ${progressPct}%, #E3E1DE ${progressPct}%, #E3E1DE 100%)`,
                    }}
                  />
                  <span className="font-mono text-[11px] text-ink-muted tabular-nums shrink-0">
                    {fmt(dur)}
                  </span>
                </div>

                <div
                  ref={convRef}
                  data-testid="live-transcript"
                  className="mt-4 flex-1 min-h-0 overflow-y-auto space-y-2.5 pr-1"
                >
                  {transcript.length === 0 ? (
                    <p className="font-sans text-[13px] text-ink-muted">
                      No transcript was captured for this call.
                    </p>
                  ) : (
                    transcript.map((line, i) => {
                      if (i > reached) return null;
                      const isAgent = line.role === "agent";
                      const current = i === reached && !ended;
                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: reduce ? 0 : 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: reduce ? 0.001 : 0.3, ease: EASE }}
                          className={`flex items-end gap-2 ${
                            isAgent ? "justify-start" : "justify-end"
                          }`}
                          data-testid={`live-bubble-${i + 1}`}
                        >
                          {isAgent ? (
                            <span className="w-6 h-6 rounded-full bg-accent text-white text-[11px] font-semibold flex items-center justify-center shrink-0">
                              A
                            </span>
                          ) : null}
                          <div
                            className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug ${
                              isAgent
                                ? "bg-white border border-hairline text-ink rounded-bl-sm"
                                : "bg-accent text-white rounded-br-sm"
                            } ${
                              current
                                ? "ring-2 ring-accent ring-offset-1 ring-offset-[#FBFAF6]"
                                : ""
                            }`}
                          >
                            {line.text}
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </motion.div>

          {/* RIGHT — live CMS intake panel (fields extracted from the real call) */}
          <motion.div
            variants={item}
            data-testid="live-system-panel"
            className="rounded-2xl p-5 flex flex-col min-h-0 text-paper bg-[linear-gradient(155deg,#191622_0%,#211D2A_100%)]"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-paper/55">
                Intake record → CMS
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-2.5 py-1">
                <span className="relative flex w-2 h-2">
                  {playing ? (
                    <span className="live-ping absolute inline-flex w-full h-full rounded-full bg-accent" />
                  ) : null}
                  <span
                    className={`relative inline-flex w-2 h-2 rounded-full ${
                      phase === "ready" ? "bg-accent" : "bg-white/30"
                    }`}
                  />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#93B4FF]">
                  {phase === "ready" ? (ended ? "Call complete" : "Capturing…") : "Processing"}
                </span>
              </span>
            </div>

            <p className="font-sans text-[12px] text-paper/55 mt-2 leading-snug">
              Watch fields populate straight from the conversation as it plays —
              exactly what lands in your CMS.
            </p>

            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5">
                <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-paper/45 mb-1">
                  Caller
                </div>
                <LiveField label="Name" value={fieldValue("caller_name")} loading={anyExtracting} />
                <LiveField label="Type" value={fieldValue("caller_type")} loading={anyExtracting} />
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5">
                <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-paper/45 mb-1">
                  Matter
                </div>
                <LiveField label="Matter type" value={fieldValue("matter_type")} loading={anyExtracting} />
                <LiveField label="Injury" value={fieldValue("injury")} loading={anyExtracting} />
                <LiveField label="Callback" value={fieldValue("callback")} loading={anyExtracting} />
              </div>
            </div>

            <div className="mt-auto pt-4">
              {ended ? (
                <motion.div
                  initial={{ opacity: 0, y: reduce ? 0 : 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reduce ? 0.001 : 0.4, ease: EASE }}
                  data-testid="live-logged-note"
                  className="inline-flex items-center gap-1.5"
                >
                  <Check className="w-3.5 h-3.5 text-accent" strokeWidth={3} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#93B4FF]">
                    Logged to CMS · context primed
                  </span>
                </motion.div>
              ) : (
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p className="font-sans text-[12px] text-paper/60 leading-snug">
                    Every call is transcribed, structured, and written to your CMS in
                    real time — the same pipeline running for firms today.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </Stage>
  );
};

// Wrapper: live recording if a demo call was placed, else the scripted demo.
export const PlaybackSlide = ({ slide, demoCall }) => {
  if (demoCall && (demoCall.conversationId || demoCall.mode === "inbound")) {
    return <LiveCallSlide slide={slide} demoCall={demoCall} />;
  }
  return <ScriptedPlaybackSlide slide={slide} />;
};

export default PlaybackSlide;
